import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChartId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { DashboardingBootstrap } from '@blisspointmedia/bpm-types/dist/dashboarding/bootstrap';
import { awaitJSON, DashboardingLambdaFetch } from '../../utils/fetch-utils';
import { FocusedChartSummary } from './dashboard-agent-grounding';

// The company's dashboarding bootstrap (KPIs/segments/sources + saved-chart metadata), fetched once
// and shared via the React Query cache. Both the grounding preamble and the saved-chart summary
// lookup derive from it, so the underlying fetch dedupes across every consumer.
const useDashboardingBootstrap = (company: string) =>
  useQuery({
    queryKey: ['dashboarding-bootstrap', company],
    enabled: Boolean(company),
    queryFn: async () => {
      const res = await DashboardingLambdaFetch('/getDashboardingBootstrap', {
        params: { company }
      });
      return awaitJSON<DashboardingBootstrap>(res);
    }
  });

// Whether ANY of the given (dashboard-placed) charts is a period-over-period comparison chart, read
// from the bootstrap's denormalized `usesComparison` flag — so the dashboard header can show a global
// comparison date range without fetching every chart's config. Reactive via the shared bootstrap query.
export const useAnyChartUsesComparison = (company: string, chartIds: ChartId[]): boolean => {
  const { data: bootstrap } = useDashboardingBootstrap(company);

  return useMemo(() => {
    if (!bootstrap) return false;
    const comparisonIds = new Set(
      bootstrap.savedCharts.filter(c => c.usesComparison).map(c => c.id)
    );
    return chartIds.some(id => comparisonIds.has(id));
  }, [bootstrap, chartIds]);
};

// A reactive id -> summary lookup over this company's saved charts (from the bootstrap's denormalized
// metadata: title + type). The agent's per-turn focus context uses it to name a dashboard's charts to
// the agent — reliably and without waiting for each card to render. Reactive because it rides the same
// bootstrap query, so it updates when the bootstrap is invalidated after a chart is saved.
export const useSavedChartSummaries = (company: string): Map<ChartId, FocusedChartSummary> => {
  const { data: bootstrap } = useDashboardingBootstrap(company);

  return useMemo(() => {
    const byId = new Map<ChartId, FocusedChartSummary>();
    bootstrap?.savedCharts.forEach(c =>
      byId.set(c.id, { id: c.id, title: c.title, type: c.chartType })
    );
    return byId;
  }, [bootstrap]);
};
